import { useState, useEffect, useCallback } from 'react';
import { 
  Search, Filter, ChevronDown, ChevronRight, X,
  MapPin, Building2, Users, Clock, CheckCircle, AlertTriangle, 
  RefreshCw, Bed, Car, Utensils, Compass, Mountain, Heart,
  Sparkles, Music, Ticket, Clapperboard, ShoppingBag, Briefcase
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// ICON MAP FOR FAMILIES
// ============================================================================

const FAMILY_ICONS = {
  accommodation: Bed,
  mobility_rent: Car,
  food_wine: Utensils,
  experiences_guides: Compass,
  outdoor_sport: Mountain,
  wellness_care: Heart,
  family_pet_assistenza: Users,
  cleaning_turnover: Sparkles,
  private_entertainment: Music,
  event_partners: Ticket,
  event_production: Clapperboard,
  shopping_artisan: ShoppingBag,
  corporate_retreats: Briefcase,
};

// ============================================================================
// FAMILY CARD COMPONENT
// ============================================================================

const FamilyCard = ({ family, isSelected, onClick, count }) => {
  const Icon = FAMILY_ICONS[family.slug] || Building2;
  const isActive = count > 0;
  
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-xl border text-left transition-all ${
        isSelected 
          ? 'border-umbria-olive bg-umbria-olive/5 shadow-sm' 
          : isActive
            ? 'border-gray-200 hover:border-umbria-olive/50 bg-white'
            : 'border-gray-100 bg-gray-50/50 opacity-60'
      }`}
      data-testid={`family-card-${family.slug}`}
    >
      <div className="flex items-center gap-2">
        <div 
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isSelected ? 'bg-umbria-olive text-white' : 'bg-gray-100 text-gray-600'
          }`}
          style={isSelected ? { backgroundColor: family.color } : {}}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-umbria-charcoal truncate">
            {family.label_it}
          </p>
          <p className="text-xs text-gray-500">
            {count} provider
          </p>
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// VERTICAL SELECTOR
// ============================================================================

const VerticalSelector = ({ family, verticals, selectedVertical, onSelect }) => {
  if (!verticals || verticals.length === 0) return null;
  
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        Verticale specifico
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onSelect(null)}
          className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
            !selectedVertical 
              ? 'bg-umbria-olive text-white border-umbria-olive' 
              : 'bg-white text-gray-600 border-gray-200 hover:border-umbria-olive/50'
          }`}
        >
          Tutti
        </button>
        {verticals.map(v => (
          <button
            key={v.slug}
            onClick={() => onSelect(v.slug)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
              selectedVertical === v.slug 
                ? 'bg-umbria-olive text-white border-umbria-olive' 
                : 'bg-white text-gray-600 border-gray-200 hover:border-umbria-olive/50'
            }`}
          >
            {v.label_it || v.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PROVIDER CARD
// ============================================================================

const ProviderVerticalCard = ({ provider, onClick }) => {
  const validation = provider._validation || {};
  const family = provider.vertical_family;
  const Icon = FAMILY_ICONS[family] || Building2;
  const classificationStatus = provider.classification_status;
  
  return (
    <Card 
      className="hover:border-umbria-olive/50 cursor-pointer transition-all hover:shadow-md group"
      onClick={onClick}
      data-testid={`provider-card-${provider.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Left: Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="h-4 w-4 text-gray-400" />
              <h3 className="font-medium text-umbria-charcoal truncate">
                {provider.provider_name || provider.business_name || 'Senza nome'}
              </h3>
              {/* Classification Status Badge */}
              {classificationStatus === 'family_only' && (
                <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                  da completare
                </Badge>
              )}
              {classificationStatus === 'needs_review' && (
                <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                  da revisionare
                </Badge>
              )}
            </div>
            
            {provider.vertical_slug && (
              <Badge variant="outline" className="text-xs mb-2">
                {provider.vertical_slug.replace(/_/g, ' ')}
              </Badge>
            )}
            
            {/* Location */}
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              <MapPin className="h-3.5 w-3.5" />
              <span>{provider.area_operativa || provider.comune || provider.macro_area || 'N/D'}</span>
            </div>
            
            {/* Contact */}
            {provider.email && (
              <p className="text-xs text-gray-400 mt-1 truncate">{provider.email}</p>
            )}
          </div>
          
          {/* Right: Status badges */}
          <div className="flex flex-col items-end gap-2">
            {/* Admin Status */}
            {provider.admin_status && (
              <Badge className={`text-xs ${
                provider.admin_status === 'approved' 
                  ? 'bg-green-100 text-green-700' 
                  : provider.admin_status === 'registered'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-amber-100 text-amber-700'
              }`}>
                {provider.admin_status}
              </Badge>
            )}
            
            {/* Validation Status */}
            <div className="flex gap-1">
              {validation.ready_for_activation ? (
                <span className="text-green-600" title="Pronto per attivazione">
                  <CheckCircle className="h-4 w-4" />
                </span>
              ) : validation.ready_for_review ? (
                <span className="text-amber-600" title="Pronto per review">
                  <Clock className="h-4 w-4" />
                </span>
              ) : (
                <span className="text-gray-400" title="Dati incompleti">
                  <AlertTriangle className="h-4 w-4" />
                </span>
              )}
            </div>
            
            {/* Missing fields count */}
            {validation.missing_required && validation.missing_required.length > 0 && (
              <span className="text-xs text-amber-600">
                {validation.missing_required.length} campi mancanti
              </span>
            )}
          </div>
          
          <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-umbria-olive transition-colors flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ProviderVerticalSearch({ onSelectProvider }) {
  // State
  const [families, setFamilies] = useState([]);
  const [stats, setStats] = useState({});
  const [verticals, setVerticals] = useState([]);
  const [providers, setProviders] = useState([]);
  
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [readyFilter, setReadyFilter] = useState(''); // '', 'review', 'activation'
  
  const [loading, setLoading] = useState(true);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // Load families and stats on mount
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [familiesRes, statsRes] = await Promise.all([
          axios.get(`${API}/provider-schema/families`),
          axios.get(`${API}/admin/providers-vertical/stats`)
        ]);
        
        setFamilies(familiesRes.data.families || []);
        
        // Convert stats to map
        const statsMap = {};
        (statsRes.data.stats || []).forEach(s => {
          statsMap[s.family] = s.count;
        });
        setStats(statsMap);
      } catch (err) {
        console.error('Failed to load families:', err);
        toast.error('Errore caricamento famiglie');
      } finally {
        setLoading(false);
      }
    };
    
    loadInitial();
  }, []);
  
  // Load verticals when family changes
  useEffect(() => {
    if (!selectedFamily) {
      setVerticals([]);
      setSelectedVertical(null);
      return;
    }
    
    const loadVerticals = async () => {
      try {
        const res = await axios.get(`${API}/provider-schema/families/${selectedFamily}/verticals`);
        setVerticals(res.data.verticals || []);
        setSelectedVertical(null);
      } catch (err) {
        console.error('Failed to load verticals:', err);
      }
    };
    
    loadVerticals();
  }, [selectedFamily]);
  
  // Search providers
  const searchProviders = useCallback(async () => {
    setLoadingProviders(true);
    try {
      const params = new URLSearchParams();
      if (selectedFamily) params.append('family', selectedFamily);
      if (selectedVertical) params.append('vertical', selectedVertical);
      if (searchText) params.append('search', searchText);
      if (areaFilter) params.append('area', areaFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (readyFilter === 'review') params.append('ready_for_review', 'true');
      if (readyFilter === 'activation') params.append('ready_for_activation', 'true');
      params.append('limit', '50');
      
      const res = await axios.get(`${API}/admin/providers-vertical?${params}`);
      setProviders(res.data.providers || []);
    } catch (err) {
      console.error('Failed to search providers:', err);
      toast.error('Errore ricerca provider');
    } finally {
      setLoadingProviders(false);
    }
  }, [selectedFamily, selectedVertical, searchText, areaFilter, statusFilter, readyFilter]);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchProviders();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchProviders]);
  
  // Clear all filters
  const clearFilters = () => {
    setSelectedFamily(null);
    setSelectedVertical(null);
    setSearchText('');
    setAreaFilter('');
    setStatusFilter('');
    setReadyFilter('');
  };
  
  const hasFilters = selectedFamily || selectedVertical || searchText || areaFilter || statusFilter || readyFilter;
  
  // Calculate classification stats from providers
  const classificationStats = {
    total: providers.length,
    classified: providers.filter(p => p.classification_status === 'classified').length,
    familyOnly: providers.filter(p => p.classification_status === 'family_only').length,
    needsReview: providers.filter(p => p.classification_status === 'needs_review' || !p.vertical_family).length,
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-umbria-olive" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="provider-vertical-search">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl font-semibold text-umbria-charcoal">
            Provider per Verticale
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Cerca e filtra provider per famiglia e verticale
          </p>
        </div>
        
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4 mr-1" />
            Pulisci filtri
          </Button>
        )}
      </div>
      
      {/* Classification Stats (mini) */}
      {providers.length > 0 && (
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-gray-600">{classificationStats.classified} classificati</span>
          </span>
          {classificationStats.familyOnly > 0 && (
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-amber-600">{classificationStats.familyOnly} da completare</span>
            </span>
          )}
          {classificationStats.needsReview > 0 && (
            <span className="flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-red-600">{classificationStats.needsReview} da revisionare</span>
            </span>
          )}
        </div>
      )}
      
      {/* Family Grid */}
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-3">
          Famiglia
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {families.map(family => (
            <FamilyCard
              key={family.slug}
              family={family}
              isSelected={selectedFamily === family.slug}
              onClick={() => setSelectedFamily(
                selectedFamily === family.slug ? null : family.slug
              )}
              count={stats[family.slug] || 0}
            />
          ))}
        </div>
      </div>
      
      {/* Vertical Selector (if family selected) */}
      {selectedFamily && (
        <VerticalSelector
          family={selectedFamily}
          verticals={verticals}
          selectedVertical={selectedVertical}
          onSelect={setSelectedVertical}
        />
      )}
      
      {/* Search & Filters Row */}
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Cerca provider..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-10"
            data-testid="search-providers"
          />
        </div>
        
        {/* Area Filter */}
        <Input
          placeholder="Area / Comune..."
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          className="w-40"
          data-testid="filter-area"
        />
        
        {/* Status Filter */}
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="Stato..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="registered">Registrato</SelectItem>
            <SelectItem value="under_review">In revisione</SelectItem>
            <SelectItem value="approved">Approvato</SelectItem>
            <SelectItem value="rejected">Rifiutato</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Ready Filter */}
        <Select value={readyFilter || "all"} onValueChange={(v) => setReadyFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="filter-ready">
            <SelectValue placeholder="Prontezza..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualsiasi</SelectItem>
            <SelectItem value="review">Pronto per review</SelectItem>
            <SelectItem value="activation">Pronto per attivazione</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Results */}
      <div className="space-y-3">
        {loadingProviders ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-5 w-5 animate-spin text-umbria-olive" />
          </div>
        ) : providers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nessun provider trovato</p>
              {hasFilters && (
                <p className="text-sm text-gray-400 mt-1">
                  Prova a modificare i filtri
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              {providers.length} provider trovati
            </p>
            {providers.map(provider => (
              <ProviderVerticalCard
                key={provider.id}
                provider={provider}
                onClick={() => onSelectProvider?.(provider)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default ProviderVerticalSearch;
